import {
  DragDropProvider,
  type DragEndEvent,
  type DragStartEvent,
  useDraggable,
  useDroppable
} from "@dnd-kit/react";
import { useEffect, useMemo, useRef, useState } from "react";

import { ApiError } from "../../api/client";
import { EditorModal } from "../catalog/CatalogPages";
import { weekPatternLabel } from "../shared/weekPatterns";
import type {
  DragOptionsResponse,
  DragTarget,
  PlacementAction,
  TimetableLesson,
  TimetableSchedule,
  TimetableSlot,
  TimetableView
} from "./types";

const weekdayLabels = ["星期一", "星期二", "星期三", "星期四", "星期五"];

type LessonDragData = { lessonId: string };
type CellDropData = { weekday: number; timeSlotId: string };

interface TimetableGridBaseProps {
  view: TimetableView;
  schedule: TimetableSchedule;
  slots: TimetableSlot[];
  timetableRevision: number;
}

type EditableTimetableGridProps = TimetableGridBaseProps & {
  mode?: "EDITABLE";
  pending: boolean;
  requestOptions: (lessonId: string) => Promise<DragOptionsResponse>;
  onAdd: (weekday: number, slot: TimetableSlot) => void;
  onEdit: (lesson: TimetableLesson) => void;
  onDelete: (lesson: TimetableLesson) => void;
  onPlacement: (action: PlacementAction) => void;
};

type ReadOnlyTimetableGridProps = TimetableGridBaseProps & {
  mode: "READ_ONLY";
};

export function TimetableGrid(
  props: EditableTimetableGridProps | ReadOnlyTimetableGridProps
): React.JSX.Element {
  if (props.mode === "READ_ONLY") {
    return <ReadOnlyTimetableGrid {...props} />;
  }
  return <EditableTimetableGrid {...props} />;
}

function EditableTimetableGrid({
  view,
  schedule,
  slots,
  timetableRevision,
  pending,
  requestOptions,
  onAdd,
  onEdit,
  onDelete,
  onPlacement
}: EditableTimetableGridProps): React.JSX.Element {
  const [activeLessonId, setActiveLessonId] = useState<string | null>(null);
  const [dragOptions, setDragOptions] = useState<DragOptionsResponse | null>(
    null
  );
  const [optionsPending, setOptionsPending] = useState(false);
  const [optionsError, setOptionsError] = useState<unknown>(null);
  const [keyboardLesson, setKeyboardLesson] =
    useState<TimetableLesson | null>(null);
  const [keyboardOptions, setKeyboardOptions] =
    useState<DragOptionsResponse | null>(null);
  const [selectedTargetKey, setSelectedTargetKey] = useState("");
  const activeRequest = useRef(0);
  const topScrollRef = useRef<HTMLDivElement>(null);
  const gridScrollRef = useRef<HTMLDivElement>(null);
  const lessonByCell = useMemo(
    () =>
      new Map(
        schedule.lessons.map((lesson) => [
          cellKey(lesson.weekday, lesson.time_slot_id),
          lesson
        ])
      ),
    [schedule.lessons]
  );
  const lessonById = useMemo(
    () => new Map(schedule.lessons.map((lesson) => [lesson.id, lesson])),
    [schedule.lessons]
  );
  const targets = useMemo(
    () =>
      new Map(
        (dragOptions?.targets ?? []).map((target) => [
          cellKey(target.weekday, target.time_slot_id),
          target
        ])
      ),
    [dragOptions]
  );

  useEffect(() => {
    if (
      dragOptions &&
      dragOptions.timetable_revision !== timetableRevision
    ) {
      setActiveLessonId(null);
      setDragOptions(null);
      setKeyboardLesson(null);
      setKeyboardOptions(null);
    }
  }, [dragOptions, timetableRevision]);

  async function loadDragOptions(lessonId: string): Promise<void> {
    const requestId = activeRequest.current + 1;
    activeRequest.current = requestId;
    setActiveLessonId(lessonId);
    setDragOptions(null);
    setOptionsError(null);
    setOptionsPending(true);
    try {
      const result = await requestOptions(lessonId);
      if (activeRequest.current === requestId) {
        setDragOptions(result);
      }
    } catch (error) {
      if (activeRequest.current === requestId) {
        setOptionsError(error);
      }
    } finally {
      if (activeRequest.current === requestId) {
        setOptionsPending(false);
      }
    }
  }

  function handleDragStart(event: DragStartEvent): void {
    const lessonId = String(event.operation.source?.data.lessonId ?? "");
    if (lessonId) void loadDragOptions(lessonId);
  }

  function handleDragEnd(event: DragEndEvent): void {
    const sourceId = String(
      event.operation.source?.data.lessonId ?? activeLessonId ?? ""
    );
    const options = dragOptions;
    const targetData = event.operation.target?.data as
      | CellDropData
      | undefined;
    const source = lessonById.get(sourceId);
    const target =
      targetData && options
        ? targets.get(cellKey(targetData.weekday, targetData.timeSlotId))
        : undefined;
    setActiveLessonId(null);
    setDragOptions(null);
    setOptionsPending(false);
    activeRequest.current += 1;
    if (
      event.canceled ||
      !source ||
      !target ||
      !options ||
      (target.kind !== "MOVE" && target.kind !== "SWAP")
    ) {
      return;
    }
    onPlacement({
      kind: target.kind,
      sourceLesson: source,
      target,
      expectedRevision: options.timetable_revision
    });
  }

  async function openKeyboardMove(lesson: TimetableLesson): Promise<void> {
    setOptionsError(null);
    setOptionsPending(true);
    try {
      const result = await requestOptions(lesson.id);
      setKeyboardLesson(lesson);
      setKeyboardOptions(result);
      setSelectedTargetKey("");
    } catch (error) {
      setOptionsError(error);
    } finally {
      setOptionsPending(false);
    }
  }

  const keyboardTargets = keyboardOptions?.targets ?? [];
  const selectableKeyboardTargetCount = keyboardTargets.filter(
    (target) => target.kind === "MOVE" || target.kind === "SWAP"
  ).length;
  const invalidKeyboardTargetCount = keyboardTargets.filter(
    (target) => target.kind === "INVALID"
  ).length;
  return (
    <article className="timetable-schedule">
      <header className="timetable-object-heading">
        <div>
          <h2>
            {schedule.object.label}
            {schedule.object.is_deleted ? (
              <span className="deleted-marker">已删除</span>
            ) : null}
          </h2>
          <p>{schedule.lessons.length} 节实际课程</p>
        </div>
        {optionsPending ? (
          <span className="inline-status">正在计算可用位置…</span>
        ) : null}
      </header>
      {optionsError ? (
        <p className="error-message compact" role="alert">
          {optionsError instanceof ApiError
            ? optionsError.message
            : "无法计算可用位置，请重试"}
        </p>
      ) : null}
      <div
        ref={topScrollRef}
        className="timetable-top-scroll"
        tabIndex={0}
        role="region"
        aria-label="左右滚动课表"
        onScroll={(event) => {
          if (gridScrollRef.current) {
            gridScrollRef.current.scrollLeft = event.currentTarget.scrollLeft;
          }
        }}
      >
        <div className="timetable-top-scroll-track" aria-hidden="true" />
      </div>
      <div
        ref={gridScrollRef}
        className="grid-scroll timetable-grid-scroll"
        tabIndex={0}
        onScroll={(event) => {
          if (topScrollRef.current) {
            topScrollRef.current.scrollLeft = event.currentTarget.scrollLeft;
          }
        }}
      >
        <DragDropProvider
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <table className="schedule-grid timetable-grid">
            <thead>
              <tr>
                <th>节次</th>
                {weekdayLabels.map((label) => (
                  <th key={label}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {slots.map((slot) => (
                <tr key={slot.id}>
                  <th>
                    <span>{slot.day_part === "AM" ? "上午" : "下午"}</span>
                    {slot.display_time}
                  </th>
                  {weekdayLabels.map((_label, index) => {
                    const weekday = index + 1;
                    const key = cellKey(weekday, slot.id);
                    return (
                      <TimetableCell
                        key={key}
                        weekday={weekday}
                        slot={slot}
                        lesson={lessonByCell.get(key)}
                        view={view}
                        target={activeLessonId ? targets.get(key) : undefined}
                        dragging={activeLessonId !== null}
                        disabled={pending}
                        addDisabled={
                          schedule.object.is_deleted ||
                          activeLessonId !== null
                        }
                        onAdd={onAdd}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onMove={(lesson) => void openKeyboardMove(lesson)}
                      />
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </DragDropProvider>
      </div>
      {keyboardLesson && keyboardOptions ? (
        <EditorModal
          title="移动或交换课程"
          submitLabel="继续"
          submitDisabled={!selectedTargetKey}
          wide
          pending={false}
          error={null}
          onClose={() => {
            setKeyboardLesson(null);
            setKeyboardOptions(null);
          }}
          onSubmit={() => {
            const target = keyboardTargets.find(
              (item) =>
                cellKey(item.weekday, item.time_slot_id) === selectedTargetKey
            );
            if (!target || (target.kind !== "MOVE" && target.kind !== "SWAP")) {
              return;
            }
            onPlacement({
              kind: target.kind,
              sourceLesson: keyboardLesson,
              target,
              expectedRevision: keyboardOptions.timetable_revision
            });
            setKeyboardLesson(null);
            setKeyboardOptions(null);
          }}
        >
          <div className="move-target-heading">
            <div>
              <strong id="move-target-label">选择目标位置</strong>
              <span>不可用的位置仍会展示，并说明冲突原因。</span>
            </div>
            <span>
              {selectableKeyboardTargetCount} 个可用
              {invalidKeyboardTargetCount > 0
                ? ` · ${invalidKeyboardTargetCount} 个冲突`
                : ""}
            </span>
          </div>
          <div
            className="move-target-groups"
            role="radiogroup"
            aria-labelledby="move-target-label"
          >
            {weekdayLabels.map((weekdayLabel, index) => (
              <section className="move-target-day" key={weekdayLabel}>
                <h3>{weekdayLabel}</h3>
                <div className="move-target-options">
                  {keyboardTargets
                    .filter((target) => target.weekday === index + 1)
                    .map((target) => {
                      const key = cellKey(target.weekday, target.time_slot_id);
                      const selectable =
                        target.kind === "MOVE" || target.kind === "SWAP";
                      const warningText = target.warnings
                        .map((warning) => warning.message)
                        .filter((message, messageIndex, messages) =>
                          messages.indexOf(message) === messageIndex
                        )
                        .join("；");
                      return (
                        <label
                          className={`move-target-option${
                            selectable ? "" : " is-disabled"
                          }${
                            target.warnings.length > 0 ? " has-warning" : ""
                          }${
                            selectedTargetKey === key ? " is-selected" : ""
                          }`}
                          key={key}
                        >
                          <input
                            className="move-target-radio"
                            type="radio"
                            name={`move-target-${keyboardLesson.id}`}
                            value={key}
                            checked={selectedTargetKey === key}
                            disabled={!selectable}
                            onChange={(event) =>
                              setSelectedTargetKey(event.target.value)
                            }
                          />
                          <span className="move-target-copy">
                            <strong>
                              {slotLabel(target.time_slot_id, slots)}
                            </strong>
                            <span>
                              {targetActionLabel(target, lessonById)}
                            </span>
                            {target.kind === "INVALID" ? (
                              <small>{target.invalid_reason ?? "当前不可用"}</small>
                            ) : target.kind === "CURRENT" ? (
                              <small>无需操作</small>
                            ) : warningText ? (
                              <small>需确认：{warningText}</small>
                            ) : (
                              <small>可选</small>
                            )}
                          </span>
                        </label>
                      );
                    })}
                </div>
              </section>
            ))}
          </div>
          {selectableKeyboardTargetCount === 0 ? (
            <p className="warning-text">当前没有其他可用位置。</p>
          ) : null}
        </EditorModal>
      ) : null}
    </article>
  );
}

function ReadOnlyTimetableGrid({
  view,
  schedule,
  slots
}: ReadOnlyTimetableGridProps): React.JSX.Element {
  const topScrollRef = useRef<HTMLDivElement>(null);
  const gridScrollRef = useRef<HTMLDivElement>(null);
  const lessonByCell = useMemo(
    () =>
      new Map(
        schedule.lessons.map((lesson) => [
          cellKey(lesson.weekday, lesson.time_slot_id),
          lesson
        ])
      ),
    [schedule.lessons]
  );
  return (
    <article className="timetable-schedule is-read-only">
      <header className="timetable-object-heading">
        <div>
          <h2>{schedule.object.label}</h2>
          <p>{schedule.lessons.length} 节实际课程 · 只读预览</p>
        </div>
      </header>
      <div
        ref={topScrollRef}
        className="timetable-top-scroll"
        tabIndex={0}
        role="region"
        aria-label="左右滚动候选课表"
        onScroll={(event) => {
          if (gridScrollRef.current) {
            gridScrollRef.current.scrollLeft = event.currentTarget.scrollLeft;
          }
        }}
      >
        <div className="timetable-top-scroll-track" aria-hidden="true" />
      </div>
      <div
        ref={gridScrollRef}
        className="grid-scroll timetable-grid-scroll"
        tabIndex={0}
        onScroll={(event) => {
          if (topScrollRef.current) {
            topScrollRef.current.scrollLeft = event.currentTarget.scrollLeft;
          }
        }}
      >
        <table className="schedule-grid timetable-grid">
          <thead>
            <tr>
              <th>节次</th>
              {weekdayLabels.map((label) => (
                <th key={label}>{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {slots.map((slot) => (
              <tr key={slot.id}>
                <th>
                  <span>{slot.day_part === "AM" ? "上午" : "下午"}</span>
                  {slot.display_time}
                </th>
                {weekdayLabels.map((_label, index) => {
                  const lesson = lessonByCell.get(cellKey(index + 1, slot.id));
                  return (
                    <td key={`${index + 1}:${slot.id}`}>
                      {lesson ? (
                        <ReadOnlyLessonCard lesson={lesson} view={view} />
                      ) : (
                        <span className="read-only-empty-cell">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </article>
  );
}

function ReadOnlyLessonCard({
  lesson,
  view
}: {
  lesson: TimetableLesson;
  view: TimetableView;
}): React.JSX.Element {
  return (
    <div className="lesson-card read-only-lesson-card">
      <div className="lesson-card-heading">
        <strong>{referenceLabel(lesson.course)}</strong>
      </div>
      <span>
        {view === "CLASS"
          ? `主教：${referenceLabel(lesson.main_teacher)}`
          : `班级：${referenceLabel(lesson.school_class)}`}
      </span>
      {view === "ROOM" ? (
        <span>主教：{referenceLabel(lesson.main_teacher)}</span>
      ) : null}
      <span className="lesson-week-pattern">
        {weekPatternLabel(lesson.week_pattern)}
      </span>
      {view !== "TEACHER" && lesson.assistant_teachers.length > 0 ? (
        <span>助教：{lesson.assistant_teachers.map(referenceLabel).join("、")}</span>
      ) : null}
      {view === "TEACHER" ? (
        <span>本人：{lesson.teacher_role === "MAIN" ? "主教" : "助教"}</span>
      ) : null}
      {view !== "ROOM" && lesson.room_instance ? (
        <span>所在功能教室：{referenceLabel(lesson.room_instance)}</span>
      ) : null}
    </div>
  );
}

function TimetableCell({
  weekday,
  slot,
  lesson,
  view,
  target,
  dragging,
  disabled,
  addDisabled,
  onAdd,
  onEdit,
  onDelete,
  onMove
}: {
  weekday: number;
  slot: TimetableSlot;
  lesson: TimetableLesson | undefined;
  view: TimetableView;
  target: DragTarget | undefined;
  dragging: boolean;
  disabled: boolean;
  addDisabled: boolean;
  onAdd: (weekday: number, slot: TimetableSlot) => void;
  onEdit: (lesson: TimetableLesson) => void;
  onDelete: (lesson: TimetableLesson) => void;
  onMove: (lesson: TimetableLesson) => void;
}): React.JSX.Element {
  const enabledTarget =
    target?.kind === "MOVE" || target?.kind === "SWAP";
  const { ref, isDropTarget } = useDroppable<CellDropData>({
    id: `cell:${weekday}:${slot.id}`,
    data: { weekday, timeSlotId: slot.id },
    disabled: !dragging || !enabledTarget
  });
  const targetClass = !dragging
    ? ""
    : enabledTarget
      ? "valid-target"
      : target?.kind === "INVALID"
        ? "invalid-target"
        : "";
  return (
    <td
      ref={ref}
      className={`${targetClass} ${isDropTarget ? "drop-target" : ""}`}
      aria-label={
        target?.invalid_reason
          ? `${weekdayLabels[weekday - 1]} ${slot.display_time} ${target.invalid_reason}`
          : undefined
      }
    >
      {lesson ? (
        <LessonCard
          lesson={lesson}
          view={view}
          disabled={disabled}
          onEdit={onEdit}
          onDelete={onDelete}
          onMove={onMove}
        />
      ) : (
        <button
          className="add-lesson-button"
          type="button"
          disabled={disabled || addDisabled}
          onClick={() => onAdd(weekday, slot)}
        >
          添加课程
        </button>
      )}
      {dragging && enabledTarget ? (
        <span className="target-kind">
          {target.warnings.length > 0
            ? "需确认"
            : target.kind === "MOVE"
              ? "可移动"
              : "可交换"}
        </span>
      ) : null}
    </td>
  );
}

function LessonCard({
  lesson,
  view,
  disabled,
  onEdit,
  onDelete,
  onMove
}: {
  lesson: TimetableLesson;
  view: TimetableView;
  disabled: boolean;
  onEdit: (lesson: TimetableLesson) => void;
  onDelete: (lesson: TimetableLesson) => void;
  onMove: (lesson: TimetableLesson) => void;
}): React.JSX.Element {
  const { ref, handleRef, isDragging } = useDraggable<LessonDragData>({
    id: `lesson:${lesson.id}`,
    data: { lessonId: lesson.id },
    disabled
  });
  return (
    <div
      ref={ref}
      className={`lesson-card ${isDragging ? "dragging" : ""}`}
    >
      <div className="lesson-card-heading">
        <strong>{referenceLabel(lesson.course)}</strong>
        <button
          ref={handleRef}
          className="drag-handle"
          type="button"
          disabled={disabled}
          aria-label={`拖动${lesson.course.label}`}
          title="拖动课程"
        >
          ⋮⋮
        </button>
      </div>
      <span>
        {view === "CLASS"
          ? `主教：${referenceLabel(lesson.main_teacher)}`
          : `班级：${referenceLabel(lesson.school_class)}`}
      </span>
      {view === "ROOM" ? (
        <span>主教：{referenceLabel(lesson.main_teacher)}</span>
      ) : null}
      <span className="lesson-week-pattern">{weekPatternLabel(lesson.week_pattern)}</span>
      {lesson.course_rule_override_messages.length > 0 ? (
        <span
          className="course-rule-override-badge"
          title={lesson.course_rule_override_messages.join("；")}
        >
          人工覆盖规则
        </span>
      ) : null}
      {view !== "TEACHER" && lesson.assistant_teachers.length > 0 ? (
        <span>助教：{lesson.assistant_teachers.map(referenceLabel).join("、")}</span>
      ) : null}
      {view === "TEACHER" ? (
        <span>本人：{lesson.teacher_role === "MAIN" ? "主教" : "助教"}</span>
      ) : null}
      {view !== "ROOM" && lesson.room_instance ? (
        <span>所在功能教室：{referenceLabel(lesson.room_instance)}</span>
      ) : null}
      <div className="lesson-actions">
        <button type="button" disabled={disabled} onClick={() => onEdit(lesson)}>
          修改
        </button>
        <button type="button" disabled={disabled} onClick={() => onMove(lesson)}>
          移动/交换
        </button>
        <button
          className="danger"
          type="button"
          disabled={disabled}
          onClick={() => onDelete(lesson)}
        >
          删除
        </button>
      </div>
    </div>
  );
}

/**
 * 单个对象的课程总览列表（不含面板外壳）。
 * 面板外壳（标题、收起/展开）由 TimetablePage 右侧 .overview-panel 提供。
 */
export function ScheduleOverview({
  view,
  schedule
}: {
  view: TimetableView;
  schedule: TimetableSchedule;
}): React.JSX.Element {
  if (schedule.overview.length === 0) {
    return <p className="overview-empty">暂无课程</p>;
  }
  return (
    <ul className="overview-list">
      {schedule.overview.map((item) => (
        <li
          key={`${item.course.id}-${item.school_class?.id ?? ""}-${item.teacher_role ?? ""}`}
        >
          <div>
            <strong>{referenceLabel(item.course)}</strong>
            {view !== "CLASS" ? (
              <span>
                {referenceLabel(item.school_class)}
                {view === "TEACHER" ? (
                  <>
                    {" · "}
                    {item.teacher_role === "MAIN" ? "主教" : "助教"}
                  </>
                ) : null}
              </span>
            ) : null}
          </div>
          <b>{item.lesson_count} 节</b>
        </li>
      ))}
    </ul>
  );
}

function targetActionLabel(
  target: DragTarget,
  lessons: Map<string, TimetableLesson>
): string {
  if (target.kind === "CURRENT") return "当前位置";
  const targetLesson = target.target_lesson_id
    ? lessons.get(target.target_lesson_id)
    : undefined;
  return targetLesson
    ? `与“${targetLesson.course.label}”交换`
    : "移动到空位";
}

function slotLabel(timeSlotId: string, slots: TimetableSlot[]): string {
  return slots.find((item) => item.id === timeSlotId)?.display_time ?? "未知节次";
}

function cellKey(weekday: number, timeSlotId: string): string {
  return `${weekday}:${timeSlotId}`;
}

function referenceLabel(
  reference: { label: string; is_deleted: boolean } | null
): string {
  if (!reference) return "";
  return `${reference.label}${reference.is_deleted ? "（已删除）" : ""}`;
}
